import { useState } from "react";
import Dialog from "@/components/ui/Dialog";
import TextAreaField from "@/components/input/TextAreaField";
import { useToast } from "@/context/ToastContext";
import { useUser } from "@/context/UserContext";
import { submitAcquisitionSource } from "@/services/user";
import { ACQUISITION_SOURCES } from "@/lib/constants/acquisitionSources";
import Button from "@/components/ui/Button";

interface AcquisitionSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// A grid of one-click pills, not components/ui/Select.tsx's dropdown --
// this is a one-time recall/self-report question with 14 options, all
// worth showing at once rather than hidden behind a closed dropdown, and
// it's the near-universal pattern for this exact "how did you hear about
// us" survey shape.
export default function AcquisitionSourceModal({ isOpen, onClose }: AcquisitionSourceModalProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();
  const { refetchUser } = useUser();

  const reset = () => {
    setSelected(null);
    setDetail("");
  };

  // The backdrop click and the Dialog's own header "x" both call this same
  // onClose -- routing every dismiss path (not just the explicit Skip
  // button below) through the same handler is what makes the tri-state
  // gating in acquisition_prompt_status actually hold: there is no way to
  // make this modal go away without it being recorded as skipped, so it
  // never silently re-prompts next session.
  const handleSkip = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await submitAcquisitionSource({ status: "skipped" });
      await refetchUser();
    } catch {
      // Non-blocking -- worst case this re-prompts next session, which is
      // a much better failure mode than trapping the user in a dialog they
      // can't dismiss over a one-time survey question.
    } finally {
      setIsSubmitting(false);
      reset();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!selected || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await submitAcquisitionSource({
        status: "answered",
        source: selected,
        detail: detail.trim() || undefined,
      });
      await refetchUser();
      addToast({ message: "Thanks for letting us know", type: "success" });
      reset();
      onClose();
    } catch {
      addToast({ message: "Couldn't save your answer. Try again.", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleSkip} title="How did you hear about Spidra?" width="max-w-md">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Where you heard about Spidra">
          {ACQUISITION_SOURCES.map(option => (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected === option.value}
              onClick={() => setSelected(option.value)}
              disabled={isSubmitting}
              className={`px-3 py-2.5 text-sm rounded-lg border text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                selected === option.value
                  ? "border-primary bg-primary-subtle text-primary-text font-medium"
                  : "border-line-strong text-fg-secondary hover:bg-surface-muted"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {selected && (
          <TextAreaField
            label="What were you searching for? (optional)"
            name="acquisitionDetail"
            value={detail}
            onChange={e => setDetail(e.target.value)}
            placeholder="e.g. best web scraping API for..."
            rows={2}
            maxLength={280}
            disabled={isSubmitting}
            className="dark:bg-surface dark:border-white/10 dark:text-gray-100 dark:placeholder-gray-500"
          />
        )}

        <div className="flex items-center justify-between pt-1">
          <Button variant="ghost" onClick={handleSkip} disabled={isSubmitting}>
            Skip this question
          </Button>
          <Button onClick={handleSubmit} disabled={!selected} loading={isSubmitting} loadingText="Saving">
            Submit
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
